import { useEffect } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Input } from '@factorialco/f0-react/dist/experimental'

import { observer } from 'mobx-react'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useFormContext } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import BookableEmployeeSelector from 'modules/space_control/components/BookingsSidepanel/BookableEmployeeSelector'
import BookingSlots from 'modules/space_control/components/BookingsSidepanel/BookingSlots/index'
import SidepanelInfo from 'modules/space_control/components/BookingsSidepanel/SidepanelInfo/index'
import type { WorkSpace as WorkSpaceNode } from 'modules/space_control/hooks/useWorkSpace'
import type { WorkSpaceSlotNode } from 'modules/space_control/hooks/useWorkSpaceSlots'

const isWholeDay = (slotSetup: string | null | undefined): boolean => slotSetup === 'whole_day'

interface bookingFormFields {
  accessId: string
  date: string
  slots?: number[]
}

interface Props {
  onClose: () => void
  workSpace: WorkSpaceNode
  slots: WorkSpaceSlotNode[]
  isSlotsLoading: boolean
  edit?: boolean
  bookedSlots?: WorkSpaceSlotNode[]
  selectedDate: string
  onChangeDate?: (date: string) => void
}

const BookingSidepanel = ({
  onClose,
  workSpace,
  slots,
  isSlotsLoading,
  edit,
  bookedSlots = [],
  selectedDate,
  onChangeDate,
}: Props) => {
  const bookingForm = useFormContext<bookingFormFields>()
  const isHalfDay = workSpace?.slotSetup === 'half_day'

  const timezone = workSpace.workArea?.location?.timezone

  const handleAccessIdChange = (accessId: string | undefined) => {
    bookingForm.setFieldValue('accessId', accessId ?? '-1')
  }

  const handleSubmit = () => {
    bookingForm.handleSubmit()
    onClose()
  }

  const handleCancel = () => {
    track(trackingEvents.spaceControl.bookings.cancelBooking)
    onClose()
  }

  const onChangeTimeSlots = (value: number) => {
    const currentValues = bookingForm.values.slots
    let newSlots: number[]

    if (currentValues && currentValues?.length > 0) {
      if (currentValues?.includes(value)) {
        newSlots = currentValues.filter((slot) => slot !== value)
      } else {
        newSlots = [...currentValues, value]
      }
    } else {
      newSlots = [value]
    }

    bookingForm.setFieldValue('slots', newSlots)
  }

  useEffect(() => {
    const selectedUnavailableSlots = bookingForm.values.slots?.filter((value) =>
      bookedSlots.some((slot) => slot.slotNumber === value)
    )
    if (
      selectedUnavailableSlots &&
      selectedUnavailableSlots.length > 0 &&
      bookingForm.values.slots
    ) {
      bookingForm.setFieldValue(
        'slots',
        bookingForm.values.slots.filter((value) => !selectedUnavailableSlots.includes(value))
      )
    }
  }, [bookedSlots, bookingForm])

  const currentAccessId =
    bookingForm.values.accessId === '-1' ? undefined : bookingForm.values.accessId

  if (workSpace) {
    return (
      <Sidepanel
        onClose={onClose}
        closeLabel="close"
        title={
          edit
            ? i18n.t('space_control.overview.booking_sidepanel.edit_title')
            : i18n.t('space_control.overview.booking_sidepanel.title')
        }
        open
        footer={
          <>
            <Button onClick={handleCancel} type="secondary">
              {i18n.t('space_control.overview.booking_sidepanel.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                bookingForm.values.accessId === '-1' ||
                (!isWholeDay(workSpace.slotSetup) && bookingForm.values.slots?.length === 0) ||
                (isWholeDay(workSpace.slotSetup) && slots?.[0]?.currentCapacity === 0)
              }
            >
              {edit
                ? i18n.t('space_control.overview.booking_sidepanel.save_changes')
                : i18n.t('space_control.overview.booking_sidepanel.confirm')}
            </Button>
          </>
        }
      >
        <SidepanelInfo workSpace={workSpace} />
        <Box paddingTop="s32" gap="s24">
          <BookableEmployeeSelector
            value={currentAccessId}
            onChange={handleAccessIdChange}
            label={i18n.t('common.employee')}
          />

          {edit && (
            <Box gap="s8">
              <Input
                type="date"
                label={i18n.t('space_control.overview.booking_sidepanel.date_field_label')}
                value={bookingForm.values.date}
                onChange={(value: string) => {
                  if (onChangeDate) {
                    onChangeDate(value)
                  }
                  bookingForm.setFieldValue('date', value)
                }}
              />
              {bookingForm.errors.date && (
                <Text.Normal size="100" color="moodSuperNegative">
                  {bookingForm.errors.date}
                </Text.Normal>
              )}
            </Box>
          )}
          {!isSlotsLoading && !isWholeDay(workSpace.slotSetup) && (
            <BookingSlots
              slots={slots}
              onChange={onChangeTimeSlots}
              selectedValues={bookingForm.values.slots ?? []}
              isHalfDay={isHalfDay}
              timezone={timezone}
              selectedDate={selectedDate}
              edit={edit}
            />
          )}
        </Box>
      </Sidepanel>
    )
  }
  return <></>
}

export default observer(BookingSidepanel)
